import { lang } from '@umu/ukit-lang';
/**
 * @desc 内容折叠展示，更多全文
 * @author Kody
 */
import React, { useRef, useState, useEffect } from 'react';
import { useBodyResize } from '@umu/umu-react-hooks';
export interface IProps {
    lineHeight?: number; // 行高 px
    children: JSX.Element | string;
    foldLabel?: JSX.Element | string;
    unfoldLabel?: JSX.Element | string;
    isDefaultFull?: boolean; // 初始默认是展开状态
    maxLine?: number; // 折叠状态下的最大行数
    btnPosition?: 'normal' | 'follow'; // 折叠按钮的位置， normal：另起一行 follow：跟在最后一个文字后面
    onFoldChange?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, isFold: boolean) => void;
    overflowedCb?(): void; // 告诉外面内容是否溢出了
}

import './moreText.scss';

export const MoreText = (props: IProps) => {
    const {
        children,
        maxLine = 5,
        lineHeight: originLineHeight = 18,
        isDefaultFull = false,
        btnPosition = 'normal',
        foldLabel = lang('展开'),
        unfoldLabel = lang('收起'),
        overflowedCb,
    } = props;
    const [isFold, setIsFold] = useState(!isDefaultFull);
    const [showMoreBtn, setShowMoreBtn] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLPreElement>(null);
    const htmlFontSize = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('font-size') || '100');
    const lineHeight = Math.round((originLineHeight * htmlFontSize) / 100);
    const lineHeightPxStr = `${lineHeight}px`;
    const label = isFold ? foldLabel : unfoldLabel;

    const setShowStatus = () => {
        const el = contentRef.current;
        if (el && el.scrollHeight > lineHeight * maxLine) {
            setShowMoreBtn(true);
            if (overflowedCb) {
                overflowedCb();
            }
        } else {
            setShowMoreBtn(false);
        }
    };

    useBodyResize(setShowStatus);

    useEffect(() => {
        if (containerRef && containerRef.current) {
            const lineClamp = isFold ? maxLine : 'none';
            const height = isFold && showMoreBtn ? `${+(lineHeight * maxLine).toFixed(2)}px` : 'auto';
            containerRef.current!.setAttribute('style', `line-clamp: ${lineClamp};-webkit-line-clamp: ${lineClamp};`);
            containerRef.current!.style.height = height;
            containerRef.current!.style.lineHeight = lineHeightPxStr;

        }
    }, [isFold, showMoreBtn]);

    useEffect(setShowStatus, [children]);

    const onFoldChange = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        const fold = !isFold;
        setIsFold(fold);
        if (props.onFoldChange) {
            props.onFoldChange(e, fold);
        }
    };

    const renderNormalBtn = () => {
        if (!showMoreBtn || btnPosition !== 'normal') {
            return null;
        }
        return (
            <button className={`btn-fold ${isFold ? 'fold' : 'unfold'}`} onClick={onFoldChange}>
                {label}
                {typeof label === 'string' && <i className="icon iconfont icon-arrow-right" />}
            </button>
        );
    };

    const renderFollowBtn = () => {
        if (!showMoreBtn || btnPosition !== 'follow') {
            return null;
        }
        return (
            <button className={`btn-fold fold-follow ${isFold ? 'fold' : 'unfold'}`} style={{ lineHeight: lineHeightPxStr }} onClick={onFoldChange}>
                {isFold && <div className="follow-ahead" style={{ height: lineHeightPxStr, lineHeight: lineHeightPxStr }}></div>}
                {label}
            </button>
        );
    };

    const render = () => {
        const style = {
            lineClamp: maxLine,
            lineHeight: lineHeightPxStr,
            height: isFold && showMoreBtn ? `${+(lineHeight * maxLine).toFixed(2)}px` : 'auto',
        };
        return (
            <div className={`more-text ${btnPosition}`}>
                <div style={style} ref={containerRef} className={`text-content ${isFold ? 'fold' : 'unfold'}`}>
                    <pre className="text" ref={contentRef}>
                        {children}
                        {renderFollowBtn()}
                    </pre>
                </div>
                {renderNormalBtn()}
            </div>
        );
    };

    return render();
};
